/**
 * LastReportChip — "the report I just closed, please". Card P229 item 3.
 *
 * ⛔⛔ THE REPORT WAS NEVER LOST — THERE WAS JUST NO WAY BACK TO IT. P219 moved
 * the generation OUT of the dialog into src/lib/ceoReportJob.js precisely so
 * closing the dialog would not cancel it, and only resetJob() clears that
 * store. So after Daniel clicked outside and the popup vanished, the finished
 * brief was still sitting in memory, complete, with no control anywhere that
 * pointed at it. A minute of the model's time, one click from the reader, and
 * nothing on screen admitting it still existed.
 *
 * ⚠ NO NEW STORAGE AND NO SERVER PERSISTENCE. This reads the job store and
 * nothing else; it lives for the tab and goes on reload or sign-out, which is
 * what the card asks for and what the data deserves — a pipeline brief is
 * cheap to regenerate and expensive to invalidate correctly.
 *
 * ⚠ Download is gated on the SAME export grant as the dialog's own button.
 * A chip that offered an export the dialog refuses would be a second answer
 * to one question.
 */

import React, { useEffect, useState } from 'react';
import { subscribeJob } from '../../lib/ceoReportJob';
import { C } from './_overviewTheme';
import { formatGeneratedAt } from './_reportFormat';

export default function LastReportChip({ onView, onPrint, onDownload, canExport = false }) {
  const [job, setJob] = useState(null);
  useEffect(() => subscribeJob(setJob), []);

  /*
   * ⛔ ONLY A FINISHED, USABLE REPORT. A running job already has the working
   * panel and the toast; a failed one has the error panel. Showing a chip for
   * either would be a third place saying the same thing, and a chip reading
   * "Last AI report" over a failure would be a lie.
   */
  if (!job || job.status !== 'done' || !job.result || !job.result.report) return null;

  /*
   * ⛔⛔ CARD P234 — NEVER AN EMPTY SEGMENT BETWEEN SEPARATORS. The live chip
   * read "Last AI report ·  · Nov 1, 2026, 7:31 PM": a pipeline label that
   * arrived blank left a gap flanked by two dots, which reads as a missing
   * value rather than as "all pipelines".
   *
   * ⚠ FIXED AT THE JOIN, NOT AT THE SOURCE. A fallback on one field only
   * cures the field it is written for; building the line from a filtered
   * list means NO segment can ever render empty, whatever arrives — and a
   * blank-but-not-empty string (a stray space) is caught by the trim.
   */
  const label = String((job.scope && job.scope.pipelineLabel) || '').trim();
  const scope = label || 'All pipelines';
  const when = formatGeneratedAt(job.finishedAt);
  const line = ['Last AI report', scope, when].filter(Boolean).join(' \u00b7 ');

  const link = {
    background: 'none', border: 'none', padding: 0, font: 'inherit',
    fontSize: 12, fontWeight: 600, color: C.primary,
    textDecoration: 'underline', cursor: 'pointer',
  };

  return (
    <div data-testid="last-ai-report"
         style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  fontSize: 12, color: C.muted, padding: '4px 10px',
                  border: `1px solid ${C.border}`, borderRadius: 999, background: C.card }}>
      <span data-testid="last-ai-report-label">{line}</span>
      <button type="button" style={link} data-testid="last-ai-report-view" onClick={onView}>
        View
      </button>
      <button type="button" style={link} data-testid="last-ai-report-print" onClick={onPrint}>
        Print
      </button>
      {/* ⛔ Absent without the grant, not disabled — the same rule the dialog
          follows, so the two surfaces cannot disagree about who may export. */}
      {canExport ? (
        <button type="button" style={link} data-testid="last-ai-report-download"
                onClick={onDownload}>
          Download PDF
        </button>
      ) : null}
    </div>
  );
}
